import { type TeamSpace } from "@prismicio/editor-fields"
import {
  ActionListHeader,
  ActionListItem,
  ActionListLabel,
  Box,
  Button,
  IconButton,
  ScrollArea,
} from "@prismicio/editor-ui"
import { useLayoutEffect, useRef, useState } from "react"
import { useLocation, useSearchParams } from "react-router-dom"

import { Link } from "../../app/routing/Link"
import { mainSpace, useTeamSpaces } from "../../app/services/role"
import { useRole } from "../../app/services/user"
import { useDocumentListLocales } from "../useDocumentListLocales"
import { useDocumentListSearchParams } from "../useDocumentListSearchParams"
import {
  getSearchParamsForTeamSpaceNavigation,
  useCanManageSpaces,
} from "./TeamSpaces.utils"
import { TeamSpacesDialog } from "./TeamSpacesDialog"

export function TeamSpacesList() {
  const teamSpaces = useTeamSpaces()

  const [isOpen, setIsOpen] = useState(false)
  const [teamSpace, setTeamSpace] = useState<TeamSpace | undefined>()

  function openSettings(teamSpace?: TeamSpace) {
    setTeamSpace(teamSpace)
    setIsOpen(true)
  }

  const { locale } = useDocumentListLocales()
  const { role } = useRole(locale)
  const canManageSpaces = useCanManageSpaces()

  const teamSpacesListMinHeight = getTeamSpacesMinHeight(teamSpaces?.length)

  return (
    <>
      <ActionListHeader>
        <ActionListLabel>Spaces</ActionListLabel>
        {canManageSpaces && (
          <>
            <Button
              invisible
              size="small"
              textWeight="normal"
              onClick={() => openSettings()}
            >
              Create a Space
            </Button>
            <TeamSpacesDialog
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              teamSpace={teamSpace}
            />
          </>
        )}
      </ActionListHeader>
      <Box
        flexDirection="column"
        flexGrow={1}
        flexBasis={0}
        minHeight={teamSpacesListMinHeight}
      >
        <ScrollArea
          style={{
            padding: 1,
            gap: 8,
          }}
          scrollPaddingBlock={1}
          stableScrollbar={false}
        >
          {teamSpaces?.map((teamSpace) => (
            <TeamSpaceItem
              key={teamSpace.id}
              showSettings={canManageSpaces}
              onSettingsClick={() => openSettings(teamSpace)}
              role={role}
              teamSpace={teamSpace}
            />
          ))}
        </ScrollArea>
      </Box>
    </>
  )
}

const minimumTeamSpacesToShow = 3.5

function getTeamSpacesMinHeight(teamSpacesCount?: number) {
  const count = (teamSpacesCount ?? 0) + 1 // +1 for the main space
  return `${
    Math.min(count, minimumTeamSpacesToShow) * 40 + // item height
    Math.min(count - 1, Math.ceil(minimumTeamSpacesToShow - 1)) * 8 + // gaps between items
    2 // 1px padding for ActionListItem's outline
  }px`
}

interface TeamSpaceItemProps {
  role: string
  showSettings?: boolean
  teamSpace: TeamSpace
  onSettingsClick?: () => void
}

function TeamSpaceItem(props: TeamSpaceItemProps) {
  const { showSettings, role, teamSpace, onSettingsClick } = props

  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()
  const isWorking = pathname.startsWith("/working")
  const isArchived = pathname.startsWith("/archived")
  const isUpcoming = pathname.startsWith("/upcoming")
  const isWorkingOrArchived = isWorking || isArchived

  const pathnameToGo = isWorkingOrArchived ? "." : "/working"

  const { optimisticSearchParams, searchAction } = useDocumentListSearchParams()

  const params = getSearchParamsForTeamSpaceNavigation(
    searchParams,
    teamSpace.id,
    teamSpace.customTypes,
  )

  const disabled = isUpcoming
  const selected =
    isWorkingOrArchived &&
    (optimisticSearchParams.space ?? mainSpace.id) === teamSpace.id

  const itemRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (selected)
      itemRef.current?.scrollIntoView({
        block: "nearest",
      })
  }, [selected])

  return (
    <ActionListItem
      ref={itemRef}
      variant="compact"
      textVariant="normal"
      backgroundColor="transparent"
      startIcon="folder"
      selected={selected}
      disabled={disabled}
      endOverlay={
        showSettings &&
        !disabled && (
          <IconButton
            icon="tune"
            variant="ghost"
            size="small"
            color={selected ? "purple" : "darkGrey"}
            onClick={onSettingsClick}
            hiddenLabel={`${teamSpace.name} settings`}
          />
        )
      }
      onClick={(e) => {
        /**
         * on click with cmd/ctrl or from outside of Team Spaces - perform native navigation
         * on click - bypass native navigation, update state + query params manually
         */
        if (e.metaKey || e.ctrlKey || !isWorkingOrArchived) return

        e.preventDefault()
        searchAction.setSearch(() => params)
      }}
      description={role}
      noWrap
      asChild
    >
      <Link
        to={{
          pathname: pathnameToGo,
          search: params.toString(),
        }}
        disabled={disabled}
        title={teamSpace.name}
      >
        {teamSpace.name}
      </Link>
    </ActionListItem>
  )
}
